import {
  X,
  Mail,
  Phone,
  MapPin,
  Briefcase,
  GraduationCap,
  Award,
  FileText,
  Code,
} from 'lucide-react';
import styles from '../form-submissions.module.css';

export interface ResumeData {
  name: string;
  email: string;
  phone: string;
  location: string;
  summary: string;
  experience: {
    role: string;
    company: string;
    duration: string;
    highlights: string[];
  }[];
  education: {
    degree: string;
    institution: string;
    years: string;
    field: string;
  }[];
  skills: string[];
  publications?: {
    title: string;
    venue: string;
    year: string;
  }[];
  achievements?: string[];
}

interface ResumeModalProps {
  isOpen: boolean;
  onClose: () => void;
  resumeData: ResumeData;
}

export function ResumeModal({ isOpen, onClose, resumeData }: ResumeModalProps) {
  if (!isOpen) return null;

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.resumeModal} onClick={e => e.stopPropagation()}>
        {/* Close Button - Floating */}
        <div className={styles.resumeModalHeader}>
          <button className={styles.modalCloseBtn} onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        {/* Resume Content - Direct View */}
        <div className={styles.resumeContent}>
          <div className={styles.resumePaper}>
            {/* Resume Header */}
            <header className={styles.resumeHeader}>
              <h1 className={styles.resumeName}>{resumeData.name}</h1>
              <div className={styles.resumeContactRow}>
                <span className={styles.resumeContactItem}>
                  <Mail size={12} />
                  {resumeData.email}
                </span>
                <span className={styles.resumeContactItem}>
                  <Phone size={12} />
                  {resumeData.phone}
                </span>
                <span className={styles.resumeContactItem}>
                  <MapPin size={12} />
                  {resumeData.location}
                </span>
              </div>
            </header>

            {/* Professional Summary */}
            <section className={styles.resumeSection}>
              <h2 className={styles.resumeSectionTitle}>
                <Briefcase size={14} />
                Professional Summary
              </h2>
              <p className={styles.resumeSummaryText}>{resumeData.summary}</p>
            </section>

            {/* Experience */}
            <section className={styles.resumeSection}>
              <h2 className={styles.resumeSectionTitle}>
                <Briefcase size={14} />
                Experience
              </h2>
              {resumeData.experience.map((exp, index) => (
                <div key={index} className={styles.resumeExperienceItem}>
                  <div className={styles.resumeExpHeader}>
                    <div>
                      <div className={styles.resumeExpRole}>{exp.role}</div>
                      <div className={styles.resumeExpCompany}>{exp.company}</div>
                    </div>
                    <div className={styles.resumeExpDuration}>{exp.duration}</div>
                  </div>
                  <ul className={styles.resumeExpHighlights}>
                    {exp.highlights.map((highlight, hIndex) => (
                      <li key={hIndex}>{highlight}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </section>

            {/* Education */}
            <section className={styles.resumeSection}>
              <h2 className={styles.resumeSectionTitle}>
                <GraduationCap size={14} />
                Education
              </h2>
              {resumeData.education.map((edu, index) => (
                <div key={index} className={styles.resumeEducationItem}>
                  <div className={styles.resumeEduHeader}>
                    <div>
                      <div className={styles.resumeEduDegree}>{edu.degree}</div>
                      <div className={styles.resumeEduInstitution}>{edu.institution}</div>
                      <div className={styles.resumeEduField}>{edu.field}</div>
                    </div>
                    <div className={styles.resumeEduYears}>{edu.years}</div>
                  </div>
                </div>
              ))}
            </section>

            {/* Skills */}
            <section className={styles.resumeSection}>
              <h2 className={styles.resumeSectionTitle}>
                <Code size={14} />
                Technical Skills
              </h2>
              <div className={styles.resumeSkillsGrid}>
                {resumeData.skills.map((skill, index) => (
                  <span key={index} className={styles.resumeSkillChip}>
                    {skill}
                  </span>
                ))}
              </div>
            </section>

            {/* Publications */}
            {resumeData.publications && resumeData.publications.length > 0 && (
              <section className={styles.resumeSection}>
                <h2 className={styles.resumeSectionTitle}>
                  <FileText size={14} />
                  Publications
                </h2>
                {resumeData.publications.map((pub, index) => (
                  <div key={index} className={styles.resumePubItem}>
                    <div className={styles.resumePubTitle}>{pub.title}</div>
                    <div className={styles.resumePubMeta}>
                      {pub.venue} · {pub.year}
                    </div>
                  </div>
                ))}
              </section>
            )}

            {/* Achievements */}
            {resumeData.achievements && resumeData.achievements.length > 0 && (
              <section className={styles.resumeSection}>
                <h2 className={styles.resumeSectionTitle}>
                  <Award size={14} />
                  Achievements
                </h2>
                <ul className={styles.resumeAchievementsList}>
                  {resumeData.achievements.map((achievement, index) => (
                    <li key={index}>{achievement}</li>
                  ))}
                </ul>
              </section>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
